import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import marketplaceService from '../../../services/marketplaceService';

const TradeHistory = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    const { data, error } = await marketplaceService?.getTradeHistory(user?.id);
    if (!error) {
      setHistory(data || []);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="bg-card border border-border rounded-lg h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {history?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="History" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No trade history</h3>
          <p className="text-muted-foreground">Your completed trades will appear here</p>
        </div>
      ) : (
        history?.map(trade => {
          const isBuyer = trade?.buyer_id === user?.id;
          const otherParty = isBuyer ? trade?.seller : trade?.buyer;

          return (
            <div key={trade?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start space-x-4">
                {/* Collectible Image */}
                <div className="w-20 h-20 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                  {trade?.collectible?.primary_image_url || trade?.listing?.featured_image_url ? (
                    <img
                      src={trade?.collectible?.primary_image_url || trade?.listing?.featured_image_url}
                      alt={trade?.collectible?.name || trade?.listing?.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Image" size={24} className="text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        {trade?.collectible?.name || trade?.listing?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {isBuyer ? 'Purchased from' : 'Sold to'}: {otherParty?.full_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-primary">
                        ${parseFloat(trade?.final_price || 0)?.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {trade?.currency || 'USD'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Completed</p>
                      <p className="text-sm font-medium text-foreground">
                        {new Date(trade?.completed_at)?.toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Type</p>
                      <p className="text-sm font-medium text-foreground capitalize">
                        {trade?.collectible?.collectible_type?.replace('-', ' ')}
                      </p>
                    </div>
                  </div>

                  {/* Ratings */}
                  {(trade?.buyer_rating || trade?.seller_rating) && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-center space-x-4">
                        {isBuyer && trade?.seller_rating && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-muted-foreground">Your rating:</span>
                            <div className="flex items-center space-x-1">
                              {[...Array(5)]?.map((_, i) => (
                                <Icon
                                  key={i}
                                  name="Star"
                                  size={14}
                                  className={i < trade?.seller_rating ? "text-yellow-500" : "text-gray-300"}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {!isBuyer && trade?.buyer_rating && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-muted-foreground">Your rating:</span>
                            <div className="flex items-center space-x-1">
                              {[...Array(5)]?.map((_, i) => (
                                <Icon
                                  key={i}
                                  name="Star"
                                  size={14}
                                  className={i < trade?.buyer_rating ? "text-yellow-500" : "text-gray-300"}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default TradeHistory;
